<template>
  <div class="template_body">
    <nav-bar v-show="shouldShowNavigation"></nav-bar>
    <side-bar v-show="shouldShowNavigation"></side-bar>
    <div class="page-container">
      <div class="notificationdiv" v-if="alert">
        {{ alrtshow }}
        <span class="notificationClose" @click="campaignsEmailAlertUpdate"
          ><i class="fas fa-times"></i
        ></span>
      </div>
      <!--
        Critical configuration problems.

        Wrapped in one container rather than reusing .notificationdiv per row:
        that class carries `top: 60px`, which applied to each banner would push
        every extra one further down the page. See docs/SYSTEM_CONFIG_ALERTS.md §11.2
      -->
      <div class="system-alert-banners" v-if="showSystemAlertBanner">
        <div class="system-alert-banner">
          <i class="fas fa-exclamation-triangle sa-banner-icon"></i>
          <div class="sa-banner-text">
            <strong>Needs attention</strong>
            <span class="sa-banner-sep">—</span>

            <!-- One tag per area. Clicking a tag opens that area's settings
                 screen; clicking View all opens the full list. -->
            <a
              href="#"
              class="sa-banner-tag"
              v-for="type in systemAlertTypes"
              :key="type.key"
              :title="'Open ' + type.label + ' settings'"
              @click.prevent="goToSystemAlertType(type)"
            >
              {{ type.label }}
              <span class="sa-banner-count">{{ type.count }}</span>
            </a>

            <a href="#" class="sa-banner-fix" @click.prevent="goToSystemAlerts"
              >View all</a
            >
          </div>
          <span
            class="sa-banner-close"
            title="Hide until you sign in again"
            @click="hideSystemAlertBanner()"
            ><i class="fas fa-times"></i
          ></span>
        </div>
      </div>
      <router-view :key="$route.fullPath"></router-view>
      <!-- <div class="scrolltop">
        <div class="scroll icon" @click="goToTop">
          <i class="fa fa-4x fa-angle-up text-white"></i>
        </div>
      </div> -->
      <div class="footer">
        <div class="px-3 py-4 sm-text-center fs-13 hint-text">
          Copyright &copy; {{ moment(Date.now()).format("YYYY") }} |
          HumanFirewall<sup class="fs-14">&reg;</sup> | All Rights Reserved.
          <span class="pull-right sm-pull-reset">
            Version: <span class="hint-text">3.05</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script type="text/javascript">
import moment from "moment";
import { mapGetters } from "vuex";
import NavBar from "./root/navbar";
import SideBar from "./root/sidebar";
import { isBannerHidden, hideBanner } from "../utils/systemAlertBanner";
export default {
  components: {
    NavBar,
    SideBar,
  },
  data() {
    return {
      campaignSendFaild: "",
      alrtshow: false,
      alert: false,
      // Configuration problems grouped by area, pushed here by the bell's poll.
      systemAlertTypes: [],
      systemAlertHidden: false,
      // Auto-hide is kept apart from systemAlertHidden on purpose: that one is
      // the admin's own "close" and persists for the whole sign-in, while this
      // is just "it has had its time on screen" and resets on every return to
      // the dashboard.
      systemAlertAutoHidden: false,
      bannerAutoHideTimer: null,
    };
  },
  computed: {
    ...mapGetters(["shouldShowNavigation"]),

    /*
      Dashboard only. This shell wraps every authenticated route, so without the
      check the banner sat on top of every screen in the product — including the
      settings pages an admin opens to fix the very problem it is reporting.
      The navbar bell stays everywhere; that is what carries the alert between
      visits to the dashboard.
    */
    onDashboard() {
      return this.$route.name === "dashboard.index";
    },
    showSystemAlertBanner() {
      return (
        this.onDashboard &&
        this.systemAlertTypes.length > 0 &&
        !this.systemAlertHidden &&
        !this.systemAlertAutoHidden
      );
    },
  },

  watch: {
    showSystemAlertBanner(visible) {
      if (visible) {
        this.startBannerAutoHide();
      } else {
        this.clearBannerAutoHide();
      }
    },

    /*
      Returning to the dashboard earns the banner a fresh 30 seconds. The timer
      says "you have seen this", not "you dealt with it" — only the close button
      and actually fixing the problem do that.
    */
    onDashboard(here) {
      if (here) {
        this.systemAlertAutoHidden = false;
      }
    },
  },
  beforeMount() {
    this.getCampaignsEmailAlert();
  },
  mounted() {
    // The bell already polls every 60s; listening to it keeps the banner in
    // step without a second timer hitting the same endpoint.
    this.$bus.$on("system-alerts-updated", this.onSystemAlertsUpdated);
  },
  beforeDestroy() {
    this.$bus.$off("system-alerts-updated", this.onSystemAlertsUpdated);
    this.clearBannerAutoHide();
  },
  methods: {
    onSystemAlertsUpdated(payload) {
      if (!payload || !payload.enabled) {
        this.systemAlertTypes = [];
        return;
      }

      const banner = payload.banner || {};
      this.systemAlertTypes = banner.types || [];
      this.systemAlertHidden = isBannerHidden();
    },
    goToSystemAlerts() {
      this.$router.push({ name: "systemAlerts" }).catch(() => {});
    },
    /** A tag click opens that area's own settings screen. */
    goToSystemAlertType(type) {
      if (!type.route) {
        this.goToSystemAlerts();
        return;
      }

      this.$router
        .push({ name: type.route, params: type.params || {} })
        .catch(() => this.goToSystemAlerts());
    },

    /*
     * Closing the banner lasts for this sign-in only — see utils/systemAlertBanner.
     * The bell and the System Alerts page are never hidden by it.
     */
    hideSystemAlertBanner() {
      this.systemAlertHidden = true;
      hideBanner();
    },

    /*
     * Take the banner off screen 30 seconds after it appears.
     *
     * It is a standing warning, not news, and it sits above the page pushing
     * everything down. Half a minute is enough to read three words and click a
     * tag; after that the navbar bell carries it, which is where a persistent
     * indicator belongs.
     */
    startBannerAutoHide() {
      this.clearBannerAutoHide();

      this.bannerAutoHideTimer = setTimeout(() => {
        this.systemAlertAutoHidden = true;
        this.bannerAutoHideTimer = null;
      }, 30000);
    },

    clearBannerAutoHide() {
      if (this.bannerAutoHideTimer) {
        clearTimeout(this.bannerAutoHideTimer);
        this.bannerAutoHideTimer = null;
      }
    },
    goToTop() {
      $("html,body").animate({ scrollTop: 0 }, "1000");
    },
    getCampaignsEmailAlert() {
      let companyLogoData = JSON.parse(localStorage.getItem("company_logo"));
      // company_logo (and its nested alert) may be missing — guard so a null
      // deref doesn't throw in beforeMount and abort the page mount.
      if (!companyLogoData || !companyLogoData.alert) {
        return;
      }
      this.alrtshow = companyLogoData.alert.msg;
      this.alert = companyLogoData.alert.campaignSendFaild;
      this.campaignSendFaild = companyLogoData.alert.id;
    },
    campaignsEmailAlertUpdate() {
      let data = { id: this.campaignSendFaild };
      this.$http.post("/setting/alert/update", data).then((response) => {
        this.alert = false;
      });
    },
  },
};

$(window).scroll(function () {
  if ($(this).scrollTop() > 50) {
    $(".scrolltop:hidden").stop(true, true).fadeIn();
  } else {
    $(".scrolltop").stop(true, true).fadeOut();
  }
});
</script>

<style scoped>
/*
  System alert banners.

  These sit as a direct child of .page-container, and that is what the sidebar
  offset has to work around. pages.css moves the offset depending on the sidebar
  state:

      .page-container                      { padding-left: 70px; }   collapsed
      body.menu-pin .page-container        { padding-left: 0; }      pinned
      body.menu-pin ... .content           { padding-left: 250px; }  pinned

  So when the sidebar is pinned the container's padding disappears and the 250px
  offset lives on .page-content-wrapper .content instead — which this banner is
  not inside. Left unhandled it starts at x=0 and the first 250px of every
  message hides underneath the fixed sidebar, which is exactly what happened.

  The rules below mirror that same switch so the banner lines up with the page
  content in both states: indented while the sidebar is pinned, flush from the
  collapsed rail otherwise.
*/
/*
  `top: 60px` shifts the banner below the fixed navbar, but a relative offset
  leaves its layout box back at the top of .page-container — so the strip it is
  actually drawn in overlaps the top padding of .page-content-wrapper, which
  comes later in the DOM and is itself positioned. Later positioned sibling wins,
  so the wrapper painted over the banner and swallowed every click on it: the
  tags, View all and the × all looked fine and did nothing.

  A z-index puts the banner back on top. Layout is untouched — .content already
  starts at the banner's bottom edge, so nothing moves. 20 rather than 2 because
  .content itself declares z-index: 10.
*/
.system-alert-banners {
  position: relative;
  top: 60px;
  z-index: 20;
}

/*
  The banner is inset to the same box the page content sits in, rather than
  running edge to edge. Measured against a page card, which spans:

      collapsed sidebar   85 .. 1585   (page-container padding-left 70 + 15)
      pinned sidebar     265 .. 1585   (250 sidebar + 15)

  so the gap is 15px on the right always, and on the left 15px plus whatever the
  sidebar is taking. Margin, not padding: the tinted strip itself has to stop at
  the gap, otherwise the colour still touches both edges.
*/
.system-alert-banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  /* anchors the absolutely-positioned close button */
  position: relative;
  margin: 5px 15px 10px 15px;
  padding: 10px 46px 10px 15px;
  line-height: 18px;
  border: 1px solid #f0d4d8;
  border-radius: 4px;
  background-color: rgba(255, 192, 203, 0.4);
  transition: margin-left 0.3s ease;
}

/* Pinned sidebar: clear the 250px the page content is given. */
@media only screen and (min-width: 980px) {
  body.menu-pin .system-alert-banner {
    margin-left: 265px;
  }
}

/* Sidebar hidden entirely — content starts from the very left. */
body.full_view .system-alert-banner {
  margin-left: 15px;
}

.sa-banner-icon {
  color: #c0392b;
  margin-top: 2px;
  flex: 0 0 auto;
}

/*
  min-width:0 lets this shrink below its content width, so a long message wraps
  instead of forcing the row wider than the viewport.
*/
.sa-banner-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
}

.sa-banner-fix {
  margin-left: 8px;
  font-weight: 600;
  white-space: nowrap;
}

.sa-banner-sep {
  margin: 0 6px 0 2px;
  opacity: 0.6;
}

/* One tag per broken area. Reads as a chip, behaves as a link. */
.sa-banner-tag {
  display: inline-block;
  margin-right: 6px;
  padding: 1px 9px;
  border: 1px solid rgba(192, 57, 43, 0.45);
  border-radius: 11px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  line-height: 18px;
}

.sa-banner-tag:hover {
  background-color: rgba(192, 57, 43, 0.1);
  text-decoration: none;
}

.sa-banner-count {
  margin-left: 4px;
  opacity: 0.75;
  font-weight: 400;
}

.sa-banner-close {
  position: absolute;
  right: 10px;
  width: 28px;
  height: 28px;
  background-color: #f2f2f2;
  text-align: center;
  padding: 7px;
  box-sizing: border-box;
  border-radius: 50%;
  cursor: pointer;
  opacity: 0.6;
}

.sa-banner-close:hover {
  opacity: 1;
}
</style>



// WEBPACK FOOTER //
// main.vue?318288a2